/**
 * embedChannel — the canonical postMessage protocol between INZARA's
 * iframe embed pages and their host applications.
 *
 * Every message is envelope-shaped:
 *   { source: 'inzara.embed' | 'inzara.host', type: <string>, ...payload }
 *
 * Direction: embed → host  (emit-side, this module's `emit` helper)
 * ─────────────────────────────────────────────────────────────────
 *   viewer.identified   Auth resolved. { workspace_id, sub, roles, capabilities }
 *   content.loaded      Dashboard/report content finished loading.
 *   content.saved       Author saved edits inside a `write`-capable embed.
 *                       { resource, id, name, updated_at }
 *   content.save_failed Save round-trip failed. { resource, id, message }
 *   content.restored    Author rolled back to a prior snapshot.
 *                       { resource, id, from_version_id, widget_count }
 *   insights.loaded     Insights list finished loading. { count }
 *   kpi.loaded          KPI Monitor finished loading. { count }
 *   drill.request       Canonical drill event. { from, target }
 *   insight.opened      LEGACY — kept for @inzara/embed-react ≤ v0.3.
 *   kpi.opened          LEGACY — kept for @inzara/embed-react ≤ v0.3.
 *   filter.applied      User set a filter inside the iframe. { key, value, previous? }
 *   filter.cleared      User cleared a filter inside the iframe. { key? }
 *   token.refreshed     SDK rotated the JWT (fired by the SDK, not this file).
 *   error               Anything the embed can't recover from. { code?, message }
 *
 * Direction: host → embed  (listen-side, this module's `subscribe` helper)
 * ─────────────────────────────────────────────────────────────────
 *   token.bootstrap     Initial JWT handoff. { token }
 *   token.updated       Silent-refresh token swap. { token }
 *   filter.set          Programmatic filter apply. { key, value }
 *   filter.clear        Programmatic filter reset. { key? }  (omit key = clear all)
 *   ping                Ask the embed to re-emit its current state.
 *
 * Anything unrecognized is ignored — new message types must not crash
 * legacy SDKs.
 */

const EMBED_SOURCE = 'inzara.embed';
const HOST_SOURCE  = 'inzara.host';

/**
 * Fire a message from the iframe out to the host. Fails silently if the
 * parent frame is unreachable (blocked by sandbox, browser-extension
 * interference, etc.).
 *
 * For backwards compatibility with hosts on the v0.1–v0.3 SDKs the
 * payload is delivered under a `payload` key AND spread at the top
 * level, so both `ev.workspace_id` and `ev.payload.workspace_id` keep
 * working.
 */
export function emit(type, payload) {
  if (typeof window === 'undefined') return;
  const body = payload && typeof payload === 'object' ? payload : {};
  try {
    window.parent?.postMessage(
      { source: EMBED_SOURCE, type, payload: body, ...body },
      '*',
    );
  } catch { /* no-op */ }
}

/**
 * Subscribe to inbound commands from the host. `handlers` maps message
 * type → callback. Returns an unsubscribe function.
 *
 *   useEffect(() => subscribe({
 *     'filter.set':   ({ key, value }) => setFilter(key, value),
 *     'filter.clear': ({ key })         => clearFilter(key),
 *     'ping':         () => emit('viewer.identified', currentSession),
 *   }), [session]);
 */
export function subscribe(handlers) {
  if (typeof window === 'undefined') return () => {};
  function onMessage(ev) {
    const data = ev?.data;
    if (!data || typeof data !== 'object') return;
    if (data.source !== HOST_SOURCE) return;
    const fn = handlers[data.type];
    if (typeof fn !== 'function') return;
    try { fn(data); }
    catch (e) {
      // Never let a host-side crash lock up the iframe — surface via
      // the emit channel so the SDK's onError fires.
      emit('error', { code: 'host_handler_threw', message: String(e?.message || e) });
    }
  }
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/** Emit `drill.request` canonical + a legacy alias for backwards compat. */
export function emitDrill(from, target) {
  emit('drill.request', { from, target });
  if (from === 'insight') emit('insight.opened', target);
  if (from === 'kpi')     emit('kpi.opened',     target);
}
